import React, { useState } from 'react';
import { STORE_INFO } from '../constants';
import { CheckCircle2, XCircle, Truck, PackageOpen, Leaf, Recycle } from 'lucide-react';

const DonationSection: React.FC = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate network request
    setTimeout(() => setFormStatus('success'), 1500);
  };

  return (
    <section id="donations" className="py-20 bg-stone-100 text-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-green-700 font-bold text-xs uppercase tracking-widest mb-4">
             <Recycle className="w-4 h-4" /> Community Resource
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">
            Keeping Stories Out of the Trash
          </h2>
          <div className="h-1 w-20 bg-brand-primary mx-auto rounded-full"></div>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-lg">
            We make it easy to clear your shelves. Join our mission to keep books and media in circulation and <strong>out of the landfill</strong>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {/* Information Column */}
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-stone-200">
              <h3 className="text-2xl font-serif font-bold text-slate-800 mb-4 flex items-center gap-2">
                <PackageOpen className="text-brand-primary" />
                The Rehoming Service
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Most books in all genres are welcome—including fiction, non-fiction, and children's literature. 
                We also gladly accept DVDs and CDs for donation, finding them new appreciative owners in the community.
              </p>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center shrink-0">
                    <Leaf className="text-green-600 w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800">Sustainable Reading</h4>
                    <p className="text-sm text-slate-600">
                      We believe no readable book should be thrown away. We work to maximize the life of every item donated.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-brand-paper rounded-full flex items-center justify-center shrink-0">
                    <Truck className="text-brand-primary w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800">Large Lot Pickup</h4>
                    <p className="text-sm text-slate-600">
                      Clearing a whole room or library? We'll drive to you across Central Illinois to handle the rehoming for you.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Guidelines */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-stone-200">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Rehoming Guidelines</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="flex items-center gap-2 font-semibold text-green-700 mb-3">
                    <CheckCircle2 className="w-5 h-5" /> We Accept
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
                    <li>Fiction & Non-Fiction Books</li>
                    <li>DVDs & Blu-rays</li>
                    <li>Audiobooks & CDs</li>
                    <li>Children's Books</li>
                  </ul>
                </div>
                <div>
                  <h4 className="flex items-center gap-2 font-semibold text-red-700 mb-3">
                    <XCircle className="w-5 h-5" /> No Thanks
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
                    <li>Encyclopedias & Magazines</li>
                    <li>VHS Tapes</li>
                    <li>Water Damaged Items</li>
                  </ul>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-6 bg-stone-50 p-4 rounded-xl border border-stone-100 italic">
                <strong>Note:</strong> We do not accept video games or consoles as donations. We prefer to purchase these directly from you!
              </p>
            </div>
          </div>

          {/* Form Column */}
          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-lg border-t-4 border-green-600">
            <h3 className="text-2xl font-serif font-bold text-slate-800 mb-2">Request Rehoming</h3>
            <p className="text-slate-500 mb-8 text-sm">
              Ready to give your books a second life? Let us know what you have and we'll arrange a visit.
            </p>

            {formStatus === 'success' ? (
              <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded-lg text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-600" />
                <h4 className="font-bold text-lg mb-2">Request Received!</h4>
                <p>Thanks for helping us keep physical media alive. We'll be in touch shortly to coordinate rehoming.</p>
                <button 
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 text-sm font-semibold underline hover:text-green-900"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input 
                    type="text" 
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-primary outline-none transition-all"
                    placeholder="Full Name"
                  />
                  <input 
                    type="tel" 
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-primary outline-none transition-all"
                    placeholder="Phone Number"
                  />
                </div>
                <textarea 
                  rows={4}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-primary outline-none transition-all"
                  placeholder="What are we rehoming? (e.g. 5 boxes of mystery novels)"
                ></textarea>

                <div className="pt-2">
                  <button 
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-4 rounded-lg shadow-md transition-colors flex items-center justify-center gap-2"
                  >
                    {formStatus === 'submitting' ? 'Processing...' : <><Truck className="w-5 h-5" /> Request Rehoming Pickup</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;